import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Package, 
  CreditCard, 
  FileText, 
  Users, 
  LogOut,
  AlertCircle,
  Menu,
  X,
  Lock,
  UserCircle
} from 'lucide-react';

const Sidebar = ({ isOpen, onToggle }) => {
  const { user, logout, isAdmin, hasOpenCashSession } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    if (hasOpenCashSession()) {
      toast.error('Debes cerrar la caja antes de salir. Ve a Punto de Venta → Cierre de Caja');
      return;
    }
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard', end: true },
    { to: '/admin/pos', icon: ShoppingCart, label: 'Punto de Venta', testId: 'nav-pos' },
    { to: '/admin/products', icon: Package, label: 'Productos', testId: 'nav-products' },
    { to: '/admin/subscriptions', icon: CreditCard, label: 'Suscripciones', testId: 'nav-subscriptions' },
    { to: '/admin/members', icon: UserCircle, label: 'Socios', testId: 'nav-members' },
    { to: '/admin/debts', icon: AlertCircle, label: 'Deudas', testId: 'nav-debts' },
    { to: '/admin/reports', icon: FileText, label: 'Historial de Cajas', testId: 'nav-reports' },
    { to: '/admin/advanced-reports', icon: FileText, label: 'Reportes', testId: 'nav-advanced-reports' },
  ];

  if (isAdmin()) {
    navItems.push({ to: '/admin/users', icon: Users, label: 'Usuarios', testId: 'nav-users' });
  }

  return (
    <>
      {/* Mobile overlay - click to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}
      
      {/* Toggle button - always visible on mobile */}
      <button
        data-testid="sidebar-toggle"
        onClick={onToggle}
        className="fixed top-4 left-4 z-[60] p-2 bg-[#0A0A0A] border border-zinc-800 rounded-sm hover:bg-zinc-800 transition-fast lg:hidden"
      >
        {isOpen ? <X size={24} className="text-white" /> : <Menu size={24} className="text-white" />}
      </button>

      <aside 
        data-testid="sidebar"
        className={`fixed left-0 top-0 h-screen w-64 bg-[#050505] border-r border-zinc-800 flex flex-col z-[55] transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
      {/* Logo */}
      <div className="p-6 border-b border-zinc-800">
        <img
          src={`${process.env.PUBLIC_URL || ''}/fex-logo-light.png`}
          alt="FEX Fitness Experience"
          className="h-11 w-auto select-none mb-1.5"
          draggable="false"
        />
        <p className="text-xs text-zinc-500 uppercase tracking-wider">
          <span className="text-white font-bold">FEX</span><span className="text-[#00FF9F] font-bold">.FIT</span> · Gestión
        </p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testId}
            onClick={() => window.innerWidth < 1024 && onToggle()}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-sm transition-fast ${
                isActive
                  ? 'bg-[#FF3B30]/10 text-[#FF3B30] border-r-2 border-[#FF3B30]'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
              }`
            }
          >
            <item.icon size={20} />
            <span className="font-medium text-sm uppercase tracking-wide">{item.label}</span>
          </NavLink>
        ))}
        
        {/* Cerrar Sesión en el menú */}
        <button
          onClick={handleLogout}
          data-testid="nav-logout"
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-fast mt-4 border-t border-zinc-800 pt-4 ${
            hasOpenCashSession()
              ? 'text-zinc-600 cursor-not-allowed'
              : 'text-zinc-400 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10'
          }`}
        >
          {hasOpenCashSession() ? <Lock size={20} /> : <LogOut size={20} />}
          <span className="font-medium text-sm uppercase tracking-wide">
            {hasOpenCashSession() ? 'Caja Abierta' : 'Cerrar Sesión'}
          </span>
        </button>
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-zinc-800">
        {/* Cash session indicator */}
        {hasOpenCashSession() && (
          <div className="mb-3 px-3 py-2 bg-emerald-600/10 border border-emerald-600/30 rounded-sm">
            <div className="flex items-center gap-2 text-emerald-400 text-xs">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              Caja Abierta
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-zinc-800 flex items-center justify-center">
            <span className="text-sm font-bold text-[#FF3B30]">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wide">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-sm transition-fast text-sm font-medium uppercase tracking-wide ${
            hasOpenCashSession()
              ? 'bg-zinc-900 text-zinc-600 cursor-not-allowed'
              : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white'
          }`}
        >
          {hasOpenCashSession() ? <Lock size={16} /> : <LogOut size={16} />}
          {hasOpenCashSession() ? 'Caja Abierta' : 'Cerrar Sesión'}
        </button>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
